import React from 'react'
import { motion } from 'framer-motion'

const AboutSection = () => {
  const stats = [
    { value: '10+', label: 'Years Experience' },
    { value: '50+', label: 'Projects Delivered' },
    { value: '8', label: 'Team Leadership' },
    { value: '4', label: 'Industries Served' },
  ]

  return (
    <section id="about" className="py-20 relative">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              About Me
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto rounded-full" />
          </motion.div>

          {/* Stats */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="glass-light rounded-xl p-6 text-center"
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <div className="text-3xl md:text-4xl font-bold text-blue-400 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* Main content */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left column - Summary */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="glass-light rounded-2xl p-8">
                <h3 className="text-2xl font-semibold mb-4 text-white">Professional Summary</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Senior Full-stack Web Developer (Backend-heavy) with 10+ years of experience in PHP (Laravel/CodeIgniter/Yii2), 
                  JavaScript (Node, React, D3), and backend development.
                </p>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Strong knowledge in system design (HLD/LLD), design patterns and SOLID principles. Experienced in DevOps (Docker, 
                  GitHub Actions), CI/CD, and cloud platforms (AWS, Heroku).
                </p>
                <p className="text-gray-300 leading-relaxed">
                  Led agile teams in delivering healthcare, fintech, OTA, and gov-tech solutions for global clients. Fluent in English 
                  (IELTS 7.0) and actively seeking senior-level opportunities in international, remote-friendly teams.
                </p>
              </div>
            </motion.div>

            {/* Right column - Key highlights */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="glass-light rounded-2xl p-8">
                <h3 className="text-2xl font-semibold mb-6 text-white">Key Highlights</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mt-1">
                      <div className="w-2 h-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">Tech Leadership</h4>
                      <p className="text-gray-400 text-sm">Managed teams of 4-6 developers across multiple projects</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mt-1">
                      <div className="w-2 h-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">System Design Expert</h4>
                      <p className="text-gray-400 text-sm">Designed microservices architecture and CI/CD pipelines</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mt-1">
                      <div className="w-2 h-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">Global Projects</h4>
                      <p className="text-gray-400 text-sm">Delivered solutions for clients in Japan, EU, and Qatar</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mt-1">
                      <div className="w-2 h-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">Performance Optimization</h4>
                      <p className="text-gray-400 text-sm">Reduced deployment time by 20% and bugs by 40%</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default AboutSection